/**
 * ContainmentDiagram - SVG nested diagram showing taxonomy -> ontology -> knowledge graph
 *
 * The "hero visual" of the Knowledge Architecture tab.
 * Detected tier is highlighted; below = achieved, above = target.
 */

import React from 'react';
import type { KnowledgeTier } from './types';
import { TIER_COLORS } from './types';

interface ContainmentDiagramProps {
  detectedTier: KnowledgeTier;
  selectedTier: KnowledgeTier;
  onSelectTier: (tier: KnowledgeTier) => void;
  confidence: 'high' | 'medium' | 'low';
}

const TIER_ORDER: KnowledgeTier[] = ['taxonomy', 'ontology', 'knowledge_graph'];

function getTierIndex(tier: KnowledgeTier): number {
  return TIER_ORDER.indexOf(tier);
}

export const ContainmentDiagram: React.FC<ContainmentDiagramProps> = ({
  detectedTier,
  selectedTier,
  onSelectTier,
  confidence,
}) => {
  const detectedIdx = getTierIndex(detectedTier);

  const renderTierRect = (
    tier: KnowledgeTier,
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    sublabel: string
  ) => {
    const tierIdx = getTierIndex(tier);
    const isDetected = tier === detectedTier;
    const isAchieved = tierIdx <= detectedIdx;
    const isSelected = tier === selectedTier;
    const color = TIER_COLORS[tier];

    return (
      <g
        key={tier}
        className="ka-diagram-tier"
        onClick={() => onSelectTier(tier)}
        style={{ cursor: 'pointer' }}
      >
        {/* Glow effect for detected tier */}
        {isDetected && (
          <rect
            x={x - 2}
            y={y - 2}
            width={width + 4}
            height={height + 4}
            rx={16}
            ry={16}
            fill="none"
            stroke={color}
            strokeWidth={2}
            opacity={0.4}
            className="ka-diagram-glow"
          />
        )}

        {/* Main rect */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={14}
          ry={14}
          fill={isDetected ? `${color}18` : isAchieved ? `${color}0a` : 'transparent'}
          stroke={isAchieved ? color : color}
          strokeWidth={isDetected ? 2.5 : isAchieved ? 1.5 : 1}
          strokeDasharray={isAchieved ? 'none' : '6 4'}
          opacity={isAchieved ? 1 : 0.5}
        />

        {/* Selection indicator */}
        {isSelected && (
          <rect
            x={x + 1}
            y={y + 1}
            width={width - 2}
            height={height - 2}
            rx={13}
            ry={13}
            fill="none"
            stroke="#f8fafc"
            strokeWidth={1}
            opacity={0.3}
          />
        )}

        {/* Tier label - positioned in the top-left band area */}
        <text
          x={x + 14}
          y={y + 22}
          fill={isAchieved ? color : `${color}80`}
          fontSize={13}
          fontWeight={isDetected ? 700 : 600}
          fontFamily="system-ui, sans-serif"
        >
          {label}
        </text>

        {/* Sublabel - pharma vocabulary hint */}
        <text
          x={x + 14}
          y={y + 38}
          fill={isAchieved ? '#94a3b8' : '#475569'}
          fontSize={10}
          fontFamily="system-ui, sans-serif"
        >
          {sublabel}
        </text>

        {/* Status badge */}
        {isDetected && (
          <>
            <rect
              x={x + width - 85}
              y={y + 10}
              width={72}
              height={22}
              rx={11}
              fill={color}
              opacity={0.2}
            />
            <text
              x={x + width - 49}
              y={y + 25}
              fill={color}
              fontSize={10}
              fontWeight={600}
              textAnchor="middle"
              fontFamily="system-ui, sans-serif"
            >
              DETECTED
            </text>
          </>
        )}

        {!isAchieved && (
          <>
            <rect
              x={x + width - 70}
              y={y + 10}
              width={57}
              height={22}
              rx={11}
              fill="#475569"
              opacity={0.3}
            />
            <text
              x={x + width - 42}
              y={y + 25}
              fill="#64748b"
              fontSize={10}
              fontWeight={500}
              textAnchor="middle"
              fontFamily="system-ui, sans-serif"
            >
              TARGET
            </text>
          </>
        )}
      </g>
    );
  };

  return (
    <div className="ka-containment-diagram">
      <div className="ka-diagram-header">
        <h4 className="ka-section-title">Knowledge Architecture Tier</h4>
        <span className={`ka-badge ka-badge-${confidence}`}>
          {confidence} confidence
        </span>
      </div>
      <svg
        viewBox="0 0 460 230"
        className="ka-diagram-svg"
        role="img"
        aria-label={`Knowledge architecture diagram showing detected tier: ${detectedTier}`}
      >
        {/* Knowledge Graph (outermost) */}
        {renderTierRect(
          'knowledge_graph', 10, 10, 440, 210,
          'Knowledge Graph',
          'Instances, SPARQL, cross-domain linking'
        )}

        {/* Ontology (middle) */}
        {renderTierRect(
          'ontology', 35, 55, 390, 155,
          'Ontology',
          'DCAT, SHACL shapes, domain/range axioms'
        )}

        {/* Taxonomy (innermost) */}
        {renderTierRect(
          'taxonomy', 60, 100, 340, 100,
          'Taxonomy',
          'CDISC, MedDRA, ATC hierarchies'
        )}

        {/* Connecting arrows between tiers */}
        <defs>
          <marker id="ka-arrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#64748b" />
          </marker>
        </defs>

        {/* Arrow: taxonomy -> ontology */}
        <line
          x1={230} y1={97} x2={230} y2={60}
          stroke="#64748b"
          strokeWidth={1}
          strokeDasharray="3 2"
          markerEnd="url(#ka-arrow)"
          opacity={detectedIdx >= 1 ? 0.3 : 0.6}
        />

        {/* Arrow: ontology -> KG */}
        <line
          x1={230} y1={52} x2={230} y2={16}
          stroke="#64748b"
          strokeWidth={1}
          strokeDasharray="3 2"
          markerEnd="url(#ka-arrow)"
          opacity={detectedIdx >= 2 ? 0.3 : 0.6}
        />
      </svg>
      <p className="ka-diagram-hint">Click a tier to explore its details</p>
    </div>
  );
};
